import { useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { RewardChip } from '@/features/guide-rewards/components/RewardChip';
import { adminGrowthApi, adminGrowthKeys } from '../api/admin-growth.api';
import { GrowthTable, Metric, fmtNum, humanize } from './GrowthShared';

const pct = (value: number) => `${(value * 100).toFixed(1)}%`;

function Loading() {
  return (
    <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
      <CircularProgress size={28} />
    </Box>
  );
}

export function GrowthOverviewPanel({ onNavigate }: { onNavigate: (tab: string) => void }) {
  const { data, isLoading, isError } = useQuery({ queryKey: adminGrowthKeys.overview(), queryFn: adminGrowthApi.overview });
  if (isLoading) return <Loading />;
  if (isError || !data) return <Alert severity="error">Could not load the overview.</Alert>;

  const queues = [
    { label: 'Rewards awaiting approval', value: data.queues.pendingRewards, tab: 'approvals' },
    { label: 'Photos needing review', value: data.queues.photosToReview, tab: 'photos' },
    { label: 'New T-shirt requests', value: data.queues.merchToReview, tab: 'merchandise' },
    { label: 'Rewards flagged (evidence changed)', value: data.queues.flaggedRewards, tab: 'approvals' },
  ];

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
          Needs your attention
        </Typography>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
          {queues.map((queue) => (
            <Box key={queue.label} sx={{ p: 2, border: '1px solid', borderColor: queue.value ? 'warning.main' : 'divider', display: 'grid', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {queue.label}
              </Typography>
              <Typography sx={{ fontSize: '1.8rem', fontWeight: 800 }}>{fmtNum(queue.value)}</Typography>
              <Button size="small" onClick={() => onNavigate(queue.tab)} sx={{ justifySelf: 'start', px: 0 }}>
                Open →
              </Button>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
        <Metric label="Referred guides" value={fmtNum(data.totals.totalReferred)} hint={`${fmtNum(data.totals.approvedReferrals)} approved`} />
        <Metric label="Referral conversion" value={pct(data.totals.conversionRate)} />
        <Metric label="Credits issued" value={fmtNum(data.totals.creditsIssued)} hint={`${fmtNum(data.totals.creditsReversed)} reversed`} />
        <Metric label="Pending credits" value={fmtNum(data.totals.pendingCredits)} hint={`${fmtNum(data.totals.pendingRewards)} rewards`} />
      </Box>

      {Boolean(data.outbox.DEAD) && (
        <Alert severity="error">
          {data.outbox.DEAD} reward event(s) failed permanently after retries. Check server logs for “growth-outbox”.
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        Event queue — pending {fmtNum(data.outbox.PENDING)}, processing {fmtNum(data.outbox.PROCESSING)}, done {fmtNum(data.outbox.DONE)}, failed {fmtNum(data.outbox.DEAD)}
      </Typography>
    </Box>
  );
}

export function GrowthAnalyticsPanel() {
  const { data, isLoading, isError } = useQuery({ queryKey: adminGrowthKeys.analytics(), queryFn: adminGrowthApi.analytics });
  if (isLoading) return <Loading />;
  if (isError || !data) return <Alert severity="error">Could not load analytics.</Alert>;

  const photoStatuses = ['APPROVED', 'REVIEW_REQUIRED', 'REJECTED', 'PENDING', 'PROCESSING'] as const;
  const photoTypes = ['T_SHIRT_SELFIE', 'TRAVELLER_PHOTO'] as const;
  const sizes = [...new Set(data.merchandise.map((m) => m.size))].sort();
  const merchStatuses = [...new Set(data.merchandise.map((m) => m.status))];
  const palette = ['#1A2E1A', '#C8A96E', '#3D5C3D', '#E9D7AE', '#7A5F27', '#94A3B8', '#0EA5E9'];

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
        <Metric label="Total referred guides" value={fmtNum(data.totals.totalReferred)} />
        <Metric label="Approved referrals" value={fmtNum(data.totals.approvedReferrals)} hint={`Conversion ${pct(data.totals.conversionRate)}`} />
        <Metric label="Credits issued (gross)" value={fmtNum(data.totals.creditsIssued)} hint={`Adjustments ${fmtNum(data.totals.creditsAdjusted)}`} />
        <Metric label="Pending credits" value={fmtNum(data.totals.pendingCredits)} />
      </Box>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700}>Referrals (last 6 months)</Typography>
          {data.monthlyReferrals.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>No referrals yet.</Alert>
          ) : (
            <BarChart
              height={260}
              xAxis={[{ data: data.monthlyReferrals.map((m) => m.month), scaleType: 'band' }]}
              series={[
                { data: data.monthlyReferrals.map((m) => m.total), label: 'Referred', color: '#C8A96E' },
                { data: data.monthlyReferrals.map((m) => m.approved), label: 'Approved', color: '#1A2E1A' },
              ]}
            />
          )}
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700}>Photo submissions</Typography>
          {data.photos.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>No photos yet.</Alert>
          ) : (
            <BarChart
              height={260}
              xAxis={[{ data: photoTypes.map(humanize), scaleType: 'band' }]}
              series={photoStatuses.map((status, i) => ({
                label: humanize(status),
                stack: 'photos',
                color: palette[i],
                data: photoTypes.map((type) => data.photos.find((p) => p.type === type && p.status === status)?.count ?? 0),
              }))}
            />
          )}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Activity completion
        </Typography>
        <GrowthTable
          loading={false}
          rows={data.activities}
          rowKey={(row) => row.key}
          emptyText="No activities configured."
          columns={[
            { key: 'title', label: 'Activity', render: (r) => <><Typography fontWeight={600} fontSize="0.875rem">{r.title}</Typography><Typography variant="caption" color="text.secondary">{r.key}</Typography></> },
            { key: 'status', label: 'Status', render: (r) => <RewardChip label={r.isActive ? 'Active' : 'Inactive'} tone={r.isActive ? 'success' : 'neutral'} /> },
            { key: 'credited', label: 'Credited', align: 'right', render: (r) => fmtNum(r.credited) },
            { key: 'pending', label: 'Pending', align: 'right', render: (r) => fmtNum(r.pending) },
            { key: 'rejected', label: 'Rejected', align: 'right', render: (r) => fmtNum(r.rejected) },
            { key: 'reversed', label: 'Reversed', align: 'right', render: (r) => fmtNum(r.reversed) },
            { key: 'credits', label: 'Credits issued', align: 'right', render: (r) => fmtNum(r.creditsIssued) },
            { key: 'rate', label: 'Completion (of approved guides)', align: 'right', render: (r) => pct(r.completionRate) },
          ]}
        />
      </Box>

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700}>Merchandise distribution by size</Typography>
        {sizes.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>No T-shirt requests yet.</Alert>
        ) : (
          <BarChart
            height={260}
            xAxis={[{ data: sizes, scaleType: 'band' }]}
            series={merchStatuses.map((status, i) => ({
              label: humanize(status),
              stack: 'merch',
              color: palette[i % palette.length],
              data: sizes.map((size) => data.merchandise.find((m) => m.size === size && m.status === status)?.count ?? 0),
            }))}
          />
        )}
      </Box>
    </Box>
  );
}
